'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["video", "fetchVideoAttributes", "onReplace", "onOpenVideoDetails", "onUpdateConversionAsset", "onUpdateVideoParams", "onClose"];
import PropTypes from 'prop-types';
import { useState, useEffect, Fragment } from 'react';
import styled from 'styled-components';
import FormattedMessage from 'I18n/components/FormattedMessage';
import HR from 'UIComponents/elements/HR';
import UIFieldset from 'UIComponents/form/UIFieldset';
import UIIcon from 'UIComponents/icon/UIIcon';
import UIFileAttachment from 'UIComponents/attachment/UIFileAttachment';
import UILink from 'UIComponents/link/UILink';
import { KeyTypes, PopoverType } from '../../constants/popover';
import { deepCompare, pick } from '../../utils/iterables';
import { VideoParamTypes } from '../../constants/video';
import MorphingPopoverBody from '../MorphingPopoverBody';
import ExplicitSaveControls from '../ExplicitSaveControls';
import FormLabelWithHelpLink from '../FormLabelWithHelpLink';
import VideoPopoverOverlayControls from './VideoPopoverOverlayControls';
import VideoPopoverAdvancedControls from './VideoPopoverAdvancedControls';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const VideoFileAttachment = styled(UIFileAttachment).withConfig({
  displayName: "VideoPopoverBodyEdit__VideoFileAttachment",
  componentId: "ejkm7q-0"
})(["width:100%;"]);
const renderVideoPreview = ({
  videoAttrs = {},
  onOpenVideoDetails,
  onReplace
}) => {
  const {
    name
  } = videoAttrs;
  return /*#__PURE__*/_jsxs(UIFieldset, {
    className: "p-x-5 p-bottom-2",
    _size: "small",
    children: [/*#__PURE__*/_jsx(FormLabelWithHelpLink, {
      primaryText: 'RichTextLib.video.previewLabel',
      linkText: 'RichTextLib.video.replace',
      onLinkClick: onReplace
    }), /*#__PURE__*/_jsx(VideoFileAttachment, {
      fileName: /*#__PURE__*/_jsx("span", {
        "data-test-id": "video-details-name",
        children: name
      }),
      fileInfo: /*#__PURE__*/_jsx(UILink, {
        "data-test-id": "edit-video-details-link",
        onClick: onOpenVideoDetails,
        onMouseDown: evt => evt.preventDefault(),
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: 'RichTextLib.video.details'
        })
      }),
      renderPreview: /*#__PURE__*/_jsx(UIIcon, {
        className: "align-self-end",
        name: "videoFile"
      })
    })]
  });
};
const propTypes = Object.assign({}, VideoPopoverOverlayControls.propTypes, {
  video: PropTypes.object,
  videoName: PropTypes.string,
  fetchVideoAttributes: PropTypes.func.isRequired,
  onReplace: PropTypes.func.isRequired,
  onOpenVideoDetails: PropTypes.func.isRequired,
  onUpdateConversionAsset: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
});
function VideoPopoverBodyEdit(props) {
  const {
      video,
      fetchVideoAttributes,
      onReplace,
      onOpenVideoDetails,
      onUpdateConversionAsset,
      onUpdateVideoParams,
      onClose
    } = props,
    rest = _objectWithoutPropertiesLoose(props, _excluded);
  const initialVideoParams = pick(Object.values(VideoParamTypes), video);
  const [videoParams, setVideoParams] = useState(initialVideoParams);
  const [videoAttrs, setVideoAttrs] = useState({});
  const [conversionAsset, setConversionAsset] = useState(video.conversion_asset);
  useEffect(() => {
    fetchVideoAttributes(video.player_id, {
      type: video.type
    }).then(setVideoAttrs).catch(() => setVideoAttrs({}));
  }, [fetchVideoAttributes, video]);
  const handleOpenVideoDetails = () => {
    onOpenVideoDetails(video, updatedVideo => {
      setVideoAttrs({
        name: updatedVideo.name
      });
    });
  };
  const handleCloseOnEscape = evt => {
    if (evt.key === KeyTypes.ESCAPE) {
      onClose();
    }
  };
  const handleSubmit = () => {
    if (!deepCompare(conversionAsset, video.conversion_asset)) {
      onUpdateConversionAsset(conversionAsset);
    }
    if (!deepCompare(videoParams, initialVideoParams)) {
      onUpdateVideoParams(videoParams);
    }
    onClose();
  };
  const isSubmitDisabled = conversionAsset && conversionAsset.type && !conversionAsset.id;
  return /*#__PURE__*/_jsx("div", {
    className: "small-popover-body",
    tabIndex: "-1",
    children: /*#__PURE__*/_jsx(MorphingPopoverBody, {
      title: 'RichTextLib.video.popoverTitle',
      titleClassName: "p-x-5 p-top-5",
      morphButtonTitle: 'RichTextLib.video.advanced.title',
      morphedContentTitle: 'RichTextLib.video.advanced.title',
      renderMorphedContent: () => /*#__PURE__*/_jsxs(Fragment, {
        children: [/*#__PURE__*/_jsx(VideoPopoverAdvancedControls, {
          className: "p-x-5",
          videoParams: videoParams,
          onVideoParamChange: setVideoParams
        }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
          className: "m-top-3 p-x-5 p-bottom-5",
          popoverType: PopoverType.EDIT,
          isSubmitDisabled: isSubmitDisabled,
          onSubmit: handleSubmit,
          onCancel: onClose
        })]
      }),
      children: /*#__PURE__*/_jsxs("div", {
        onKeyDown: handleCloseOnEscape,
        children: [/*#__PURE__*/_jsx(VideoPopoverOverlayControls, Object.assign({}, rest, {
          className: "p-x-5 p-bottom-1",
          conversionAsset: conversionAsset,
          video: videoAttrs,
          onUpdateConversionAsset: setConversionAsset
        })), /*#__PURE__*/_jsx(HR, {
          distance: "xs"
        }), renderVideoPreview({
          videoAttrs,
          onReplace,
          onOpenVideoDetails: handleOpenVideoDetails
        }), /*#__PURE__*/_jsx(ExplicitSaveControls, {
          className: "m-top-3 p-x-5 p-bottom-5",
          popoverType: PopoverType.EDIT,
          isSubmitDisabled: isSubmitDisabled,
          onSubmit: handleSubmit,
          onCancel: onClose
        })]
      })
    })
  });
}
VideoPopoverBodyEdit.propTypes = propTypes;
VideoPopoverBodyEdit.displayName = 'VideoPopoverBodyEdit';
export default VideoPopoverBodyEdit;